import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  BarChart3, TrendingUp, Car, Bike, Users, DollarSign, Clock,
  Camera, Wifi, Eye, Award, Calendar, Activity, RefreshCw, Loader2, Crown,
  CalendarDays, Trophy, Ticket
} from 'lucide-react';
import AppFooter from '../components/AppFooter';
import BackButton from '../components/BackButton';
import RefreshButton from '../components/RefreshButton';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/dqftlupc_Recurso%2021VR%20logos.png";
const fmt = (n) => new Intl.NumberFormat('es-AR').format(n || 0);
const fmtMoney = (n) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n || 0);
const fmtDuration = (mins) => {
  if (!mins) return '0m';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h > 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const COLORS = {
  emerald: '#10b981', blue: '#3b82f6', amber: '#f59e0b', red: '#ef4444',
  purple: '#a855f7', cyan: '#06b6d4', pink: '#ec4899', slate: '#64748b'
};
const PIE_COLORS = [COLORS.emerald, COLORS.blue, COLORS.amber, COLORS.purple, COLORS.cyan, COLORS.pink];

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/analytics/dashboard`, { withCredentials: true });
      setData(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  if (!data) return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
    </div>
  );

  const k = data.kpis;

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      <header className="bg-slate-800 border-b border-slate-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <BackButton to="/dashboard" testId="analytics-back-btn" />
              <div className="border-l border-slate-700 pl-4">
                <h1 className="text-lg font-bold text-white flex items-center gap-2"><BarChart3 className="w-5 h-5 text-emerald-400" />Dashboard Analytics</h1>
                <p className="text-xs sm:text-sm text-slate-400">Metricas, comparativas y rendimiento</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <RefreshButton onClick={fetchData} loading={loading} testId="analytics-refresh" label="Refrescar" />
              <img src={LOGO_URL} alt="ParkingVision AI" className="h-8 w-auto hidden sm:block" />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl w-full mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8 flex-1 space-y-6">

        {/* ─── KPIs HERO ─── */}
        <section>
          <h2 className="text-white text-lg font-bold mb-3 flex items-center gap-2"><Activity className="w-5 h-5 text-emerald-400" />Resumen general</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Kpi icon={Car} color="emerald" label="Adentro ahora" value={fmt(k.vehicles_inside)} />
            <Kpi icon={TrendingUp} color="blue" label="Entradas hoy" value={fmt(k.entries_today)} sub={`${k.exits_today} salidas`} />
            <Kpi icon={DollarSign} color="emerald" label="Ingresos hoy" value={fmtMoney(k.revenue_today)} />
            <Kpi icon={Calendar} color="purple" label="Ingresos mes" value={fmtMoney(k.revenue_month)} sub={`${k.entries_month} ingresos`} />
            <Kpi icon={Clock} color="amber" label="Tiempo promedio" value={fmtDuration(k.avg_duration_minutes)} />
            <Kpi icon={Users} color="cyan" label="Socios activos" value={fmt(k.members_total)} sub={`${k.members_free} gratis`} />
          </div>
        </section>

        {/* ─── KPIs Estacionamiento vs Eventos ─── */}
        <section>
          <h2 className="text-white text-lg font-bold mb-3 flex items-center gap-2"><Ticket className="w-5 h-5 text-amber-400" />Estacionamiento vs Eventos</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi icon={Car} color="blue" label="Estac. hoy" value={fmtMoney(k.parking_revenue_today)} sub={`${fmt(k.parking_operations_today)} ops`} />
            <Kpi icon={CalendarDays} color="amber" label="Eventos hoy" value={fmtMoney(k.event_revenue_today)} sub={`${fmt(k.event_operations_today)} tickets`} />
            <Kpi icon={Car} color="blue" label="Estac. mes" value={fmtMoney(k.parking_revenue_month)} sub={`${fmt(k.parking_operations_month)} ops`} />
            <Kpi icon={CalendarDays} color="amber" label="Eventos mes" value={fmtMoney(k.event_revenue_month)} sub={`${fmt(k.event_operations_month)} tickets`} />
          </div>
          {k.top_event_today && (
            <div className="mt-3 rounded-xl p-4 bg-gradient-to-r from-amber-900/30 to-amber-700/10 border border-amber-700/40 flex items-center gap-3">
              <Trophy className="w-7 h-7 text-amber-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-amber-200 text-xs uppercase tracking-wide">Top evento de hoy</p>
                <p className="text-white text-lg font-bold truncate">{k.top_event_today.name}</p>
              </div>
              <div className="text-right">
                <p className="text-emerald-400 text-xl font-bold whitespace-nowrap">{fmtMoney(k.top_event_today.revenue)}</p>
                <p className="text-slate-400 text-xs">{k.top_event_today.tickets} tickets</p>
              </div>
            </div>
          )}
        </section>

        {/* ─── Tendencia split parking/eventos + Distribución por servicio ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="bg-slate-800 border-slate-700 lg:col-span-2">
            <CardHeader><CardTitle className="text-white flex items-center gap-2"><TrendingUp className="w-5 h-5 text-amber-400" />Ingresos por servicio (30 dias)</CardTitle></CardHeader>
            <CardContent>
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <BarChart data={data.daily_trend_split || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="fecha" stroke="#94a3b8" fontSize={11} />
                    <YAxis stroke="#94a3b8" fontSize={11} />
                    <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }} formatter={(v) => fmtMoney(v)} />
                    <Legend />
                    <Bar dataKey="parking" stackId="rev" fill={COLORS.blue} name="Estacionamiento" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="eventos" stackId="rev" fill={COLORS.amber} name="Eventos" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardHeader><CardTitle className="text-white flex items-center gap-2"><BarChart3 className="w-5 h-5 text-purple-400" />Distribucion por servicio (mes)</CardTitle></CardHeader>
            <CardContent>
              <div style={{ width: '100%', height: 220 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={data.service_distribution || []} cx="50%" cy="50%" labelLine={false} outerRadius={75} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {(data.service_distribution || []).map((_, i) => <Cell key={i} fill={i === 0 ? COLORS.blue : COLORS.amber} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-1 gap-2 text-sm mt-2">
                {(data.service_distribution || []).map((v, i) => (
                  <div key={i} className="flex items-center gap-2 bg-slate-700/40 rounded p-2">
                    <span className="w-3 h-3 rounded-full" style={{ background: i === 0 ? COLORS.blue : COLORS.amber }} />
                    <span className="text-slate-300 flex-1 truncate">{v.name}</span>
                    <span className="text-white font-bold ml-auto">{fmt(v.value)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ─── Ranking + Avg ticket por evento ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader><CardTitle className="text-white flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-400" />Ranking por evento (30 dias)</CardTitle></CardHeader>
            <CardContent>
              {(data.events_breakdown || []).length === 0 ? (
                <p className="text-slate-500 text-sm py-6 text-center">Sin eventos en el periodo</p>
              ) : (
                <div className="space-y-2">
                  {(data.events_breakdown || []).slice(0, 8).map((ev, i) => (
                    <div key={ev.name} className="flex items-center gap-3 p-2.5 bg-slate-700/40 rounded-lg" data-testid={`top-event-${i}`}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-amber-500 text-slate-900' : i === 1 ? 'bg-slate-400 text-slate-900' : i === 2 ? 'bg-amber-700 text-white' : 'bg-slate-700 text-slate-300'}`}>{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{ev.name}</p>
                        <p className="text-slate-400 text-xs">{ev.tickets} tickets · {fmtMoney(ev.avg_ticket)} prom.</p>
                      </div>
                      <span className="text-emerald-400 font-bold text-sm whitespace-nowrap">{fmtMoney(ev.revenue)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardHeader><CardTitle className="text-white flex items-center gap-2"><BarChart3 className="w-5 h-5 text-emerald-400" />Recaudado por tipo de evento</CardTitle></CardHeader>
            <CardContent>
              {(data.events_breakdown || []).length === 0 ? (
                <p className="text-slate-500 text-sm py-6 text-center">Sin datos</p>
              ) : (
                <div style={{ width: '100%', height: 280 }}>
                  <ResponsiveContainer>
                    <BarChart data={data.events_breakdown || []} layout="vertical" margin={{ left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis type="number" stroke="#94a3b8" fontSize={11} />
                      <YAxis type="category" dataKey="name" stroke="#94a3b8" fontSize={11} width={110} />
                      <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }} formatter={(v) => fmtMoney(v)} />
                      <Bar dataKey="revenue" fill={COLORS.amber} name="Recaudado" radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ─── Gráfico tendencia diaria ─── */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader><CardTitle className="text-white flex items-center gap-2"><TrendingUp className="w-5 h-5 text-emerald-400" />Tendencia ultimos 30 dias</CardTitle></CardHeader>
          <CardContent>
            <div style={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <AreaChart data={data.daily_trend}>
                  <defs>
                    <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={COLORS.emerald} stopOpacity={0.5} /><stop offset="95%" stopColor={COLORS.emerald} stopOpacity={0} /></linearGradient>
                    <linearGradient id="colorEnt" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={COLORS.blue} stopOpacity={0.5} /><stop offset="95%" stopColor={COLORS.blue} stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="fecha" stroke="#94a3b8" fontSize={11} />
                  <YAxis yAxisId="left" stroke="#10b981" fontSize={11} />
                  <YAxis yAxisId="right" orientation="right" stroke="#3b82f6" fontSize={11} />
                  <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }} labelStyle={{ color: '#fff' }} />
                  <Legend />
                  <Area yAxisId="left" type="monotone" dataKey="ingresos" stroke={COLORS.emerald} fillOpacity={1} fill="url(#colorRev)" name="Ingresos ($)" strokeWidth={2} />
                  <Area yAxisId="right" type="monotone" dataKey="entradas" stroke={COLORS.blue} fillOpacity={1} fill="url(#colorEnt)" name="Entradas (#)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* ─── Comparativo por sede + Distribución vehículos ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="bg-slate-800 border-slate-700 lg:col-span-2">
            <CardHeader><CardTitle className="text-white flex items-center gap-2"><BarChart3 className="w-5 h-5 text-blue-400" />Comparativo por sede (mes actual)</CardTitle></CardHeader>
            <CardContent>
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <BarChart data={data.sites_comparison} layout="vertical" margin={{ left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis type="number" stroke="#94a3b8" fontSize={11} />
                    <YAxis type="category" dataKey="name" stroke="#94a3b8" fontSize={11} width={100} />
                    <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }} formatter={(v, name) => name === 'ingresos' ? fmtMoney(v) : fmt(v)} />
                    <Legend />
                    <Bar dataKey="ingresos" fill={COLORS.emerald} name="Ingresos ($)" radius={[0, 6, 6, 0]} />
                    <Bar dataKey="entradas" fill={COLORS.blue} name="Entradas" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardHeader><CardTitle className="text-white flex items-center gap-2"><Car className="w-5 h-5 text-amber-400" />Tipo de vehiculo</CardTitle></CardHeader>
            <CardContent>
              <div style={{ width: '100%', height: 220 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={data.vehicle_type_distribution} cx="50%" cy="50%" labelLine={false} outerRadius={75} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {data.vehicle_type_distribution.map((_, i) => <Cell key={i} fill={i === 0 ? COLORS.emerald : COLORS.amber} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm mt-2">
                {data.vehicle_type_distribution.map((v, i) => (
                  <div key={i} className="flex items-center gap-2 bg-slate-700/40 rounded p-2">
                    <span className="w-3 h-3 rounded-full" style={{ background: i === 0 ? COLORS.emerald : COLORS.amber }} />
                    <span className="text-slate-300">{v.name}</span><span className="text-white font-bold ml-auto">{fmt(v.value)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ─── Pico horario ─── */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader><CardTitle className="text-white flex items-center gap-2"><Clock className="w-5 h-5 text-purple-400" />Pico de afluencia por hora (ultimos 30 dias)</CardTitle></CardHeader>
          <CardContent>
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={data.hourly_distribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="hora" stroke="#94a3b8" fontSize={10} interval={1} />
                  <YAxis stroke="#94a3b8" fontSize={11} />
                  <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }} />
                  <Bar dataKey="entradas" fill={COLORS.purple} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* ─── Top vehículos + Top socios pagadores ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader><CardTitle className="text-white flex items-center gap-2"><Award className="w-5 h-5 text-amber-400" />Vehiculos con mas tiempo total (visitantes)</CardTitle></CardHeader>
            <CardContent>
              {data.top_vehicles.length === 0 ? <p className="text-slate-500 text-sm py-6 text-center">Sin datos aun</p> : (
                <div className="space-y-2">
                  {data.top_vehicles.map((v, i) => (
                    <div key={v.plate} className="flex items-center gap-3 p-2.5 bg-slate-700/40 rounded-lg" data-testid={`top-vehicle-${i}`}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-amber-500 text-slate-900' : i === 1 ? 'bg-slate-400 text-slate-900' : i === 2 ? 'bg-amber-700 text-white' : 'bg-slate-700 text-slate-300'}`}>{i + 1}</div>
                      {v.vehicle_type === 'moto' ? <Bike className="w-4 h-4 text-slate-400" /> : <Car className="w-4 h-4 text-slate-400" />}
                      <span className="font-mono font-bold text-white">{v.plate}</span>
                      <span className="text-slate-400 text-xs">{v.visits} visitas</span>
                      <span className="ml-auto text-emerald-400 font-bold text-sm">{fmtDuration(v.total_minutes)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardHeader><CardTitle className="text-white flex items-center gap-2"><Crown className="w-5 h-5 text-amber-400" />Socios que mas pagaron (acumulado)</CardTitle></CardHeader>
            <CardContent>
              {data.top_payers.length === 0 ? <p className="text-slate-500 text-sm py-6 text-center">Sin pagos registrados aun</p> : (
                <div className="space-y-2">
                  {data.top_payers.map((p, i) => (
                    <div key={i} className="flex items-center gap-3 p-2.5 bg-slate-700/40 rounded-lg" data-testid={`top-payer-${i}`}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-amber-500 text-slate-900' : i === 1 ? 'bg-slate-400 text-slate-900' : i === 2 ? 'bg-amber-700 text-white' : 'bg-slate-700 text-slate-300'}`}>{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{p.name}</p>
                        <p className="text-slate-400 text-xs font-mono">{p.plate} · {p.payments} pagos</p>
                      </div>
                      <span className="text-emerald-400 font-bold text-sm whitespace-nowrap">{fmtMoney(p.total)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ─── OCR + Cámaras ─── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="bg-slate-800 border-slate-700 lg:col-span-2">
            <CardHeader><CardTitle className="text-white flex items-center gap-2"><Eye className="w-5 h-5 text-cyan-400" />Lecturas OCR</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <MiniStat label="Total" value={fmt(k.ocr_total)} color="text-white" />
                <MiniStat label="Ultimos 30d" value={fmt(k.ocr_30d)} color="text-cyan-400" />
                <MiniStat label="Aceptadas" value={fmt(k.ocr_accepted)} color="text-emerald-400" />
                <MiniStat label="Pendientes" value={fmt(k.ocr_pending)} color="text-amber-400" />
              </div>
              <div style={{ width: '100%', height: 180 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={data.ocr_status_distribution} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                      {data.ocr_status_distribution.map((_, i) => <Cell key={i} fill={[COLORS.emerald, COLORS.amber, COLORS.red][i]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardHeader><CardTitle className="text-white flex items-center gap-2"><Wifi className="w-5 h-5 text-emerald-400" />Camaras y conectores</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="bg-emerald-900/20 border border-emerald-800/40 rounded-lg p-3">
                <p className="text-emerald-300 text-xs uppercase tracking-wide">Uptime</p>
                <p className="text-3xl font-bold text-emerald-400">{k.uptime_pct}%</p>
                <p className="text-slate-400 text-xs">{k.connectors_online} de {k.connectors_total} conectores online</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-slate-700/40 rounded-lg p-3"><p className="text-slate-400 text-xs">Camaras</p><p className="text-2xl font-bold text-white">{fmt(k.cameras_total)}</p></div>
                <div className="bg-slate-700/40 rounded-lg p-3"><p className="text-slate-400 text-xs">Conectores</p><p className="text-2xl font-bold text-white">{fmt(k.connectors_total)}</p></div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ─── Cuotas ─── */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader><CardTitle className="text-white flex items-center gap-2"><Users className="w-5 h-5 text-pink-400" />Cuotas de socios</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-pink-900/20 border border-pink-800/40 rounded-lg p-3">
                <p className="text-pink-300 text-xs uppercase">Cobrado este mes</p>
                <p className="text-2xl font-bold text-pink-400 break-words">{fmtMoney(k.cuotas_revenue_month)}</p>
              </div>
              <div className="bg-slate-700/40 rounded-lg p-3"><p className="text-slate-400 text-xs uppercase">Pagos del mes</p><p className="text-2xl font-bold text-white">{fmt(k.cuotas_count_month)}</p></div>
              <div className="bg-slate-700/40 rounded-lg p-3"><p className="text-slate-400 text-xs uppercase">Total socios</p><p className="text-2xl font-bold text-white">{fmt(k.members_total)} <span className="text-xs text-slate-500">({k.members_free} gratis)</span></p></div>
            </div>
          </CardContent>
        </Card>
      </main>

      <AppFooter />
    </div>
  );
}

function Kpi({ icon: Icon, color, label, value, sub }) {
  const map = {
    emerald: 'text-emerald-400 bg-emerald-900/20 border-emerald-800/30',
    blue: 'text-blue-400 bg-blue-900/20 border-blue-800/30',
    amber: 'text-amber-400 bg-amber-900/20 border-amber-800/30',
    purple: 'text-purple-400 bg-purple-900/20 border-purple-800/30',
    cyan: 'text-cyan-400 bg-cyan-900/20 border-cyan-800/30',
  };
  return (
    <Card className={`border ${map[color]}`}>
      <CardContent className="p-3">
        <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide opacity-80"><Icon className="w-3.5 h-3.5" />{label}</div>
        <p className={`text-xl sm:text-2xl font-bold mt-1 break-words ${map[color].split(' ')[0]}`}>{value}</p>
        {sub && <p className="text-slate-400 text-[10px] mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function MiniStat({ label, value, color }) {
  return (
    <div className="bg-slate-700/40 rounded-lg p-2.5 text-center">
      <p className="text-slate-400 text-[10px] uppercase">{label}</p>
      <p className={`text-xl font-bold ${color}`}>{value}</p>
    </div>
  );
}
